import type { Location } from '@medplum/fhirtypes';
import { useMemo } from 'react';

import { useFhirSearch } from '@eluve/fhir-data';
import type { FhirReferenceOptions } from '@eluve/fhir-renderer';

/**
 * The org's locations, offered as the roster behind the service ↔ location
 * picker. Fetching lives here rather than in the renderer: views are handed
 * their options (see FhirReferenceOptionsProvider).
 *
 * These resources are the FHIR mirror of the eluve location tree, kept
 * current by the `sync_fhir_mirror` job (ELU-5635).
 *
 * Only active locations are offered — a location whose eluve row was deleted
 * is flagged `inactive` rather than removed, and shouldn't be a new choice.
 * Services already pointing at one keep that reference: the picker preserves
 * selections that aren't on the roster.
 */
export function useLocationOptions(): {
  options: FhirReferenceOptions;
  loading: boolean;
} {
  // Single page, no pagination: a picker over a checkbox list stops being
  // usable long before this cap, and location trees are small (tens, not
  // thousands). A tenant past the cap would silently get a truncated roster —
  // already-linked locations still round-trip, but the missing ones can't be
  // picked. Revisit as a typeahead if that ever becomes real.
  const { resources, loading } = useFhirSearch('Location', {
    status: 'active',
    _sort: 'name',
    _count: '200',
  });

  const options = useMemo(
    (): FhirReferenceOptions =>
      // No key while the search is in flight: an empty roster renders the
      // picker's empty state, and claiming "no locations" before the answer
      // arrives is worse than rendering no picker for a moment.
      loading
        ? {}
        : {
            Location: resources.filter((location): location is Location =>
              Boolean(location.id),
            ),
          },
    [resources, loading],
  );

  return { options, loading };
}
